import { CarrierVisitStatus } from '@planning/app/api'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { useTranslate } from '@tolgee/react'
import { TomTable, useDefaultMRTOptions, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_TableInstance } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { IDataExportConfig } from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useMemo } from 'react'
import {
  RailVisitTableDto,
  useRailVisitTableColumns,
} from '../../tableConfigs/useRailVisitTableColumns'
import { RailVisitTableDetailPanel } from './RailVisitTableDetailPanel'
import { RailVisitTableOptions } from './RailVisitTableOptions'

interface Props {
  items: IRailVisitItem[]
  onDelete: (id: number) => Promise<void>
  renderTableCustomActions: ({
    table,
  }: {
    table: MRT_TableInstance<RailVisitTableDto>
  }) => React.ReactNode
}

export const RailVisitTable = observer(({ items, onDelete, renderTableCustomActions }: Props) => {
  const { t } = useTranslate()
  const columns = useRailVisitTableColumns()

  const data: RailVisitTableDto[] = useMemo(() => {
    return items.map(item => {
      const moves = (item.data.dischargeEstimate ?? 0) + (item.data.loadEstimate ?? 0)
      const tracks = item.railTracks.map(t => t.name).join(', ')
      const inIds = item.data.inboundTripIds?.filter(id => id.trim() !== '').join(', ') ?? ''
      const outIds = item.data.outboundTripIds?.filter(id => id.trim() !== '').join(', ') ?? ''

      return {
        id: item.id,
        name: item.data.name ?? '',
        status: item.data.status,
        cargoType: item.data.cargoType,
        inboundTripIds: inIds,
        outboundTripIds: outIds,

        eta: item.data.eta,
        ata: item.data.ata,
        etd: item.data.etd,
        atd: item.data.atd,

        tracks: tracks,
        moves: moves,

        originalRef: item,
      }
    })
  }, [items])

  const dataExportConfig: IDataExportConfig<RailVisitTableDto> = {
    name: { header: t('name', 'Name'), type: 'string' },
    status: { header: t('status', 'Status'), type: 'string' },
    cargoType: { header: t('type', 'Type'), type: 'string' },
    inboundTripIds: { header: t('inId', 'In. ID'), type: 'string' },
    outboundTripIds: { header: t('outId', 'Out. ID'), type: 'string' },
    eta: { header: t('eta', 'ETA'), type: 'date' },
    etd: { header: t('etd', 'ETD'), type: 'date' },
    ata: { header: t('ata', 'ATA'), type: 'date' },
    atd: { header: t('atd', 'ATD'), type: 'date' },
    tracks: { header: t('tracks', 'Tracks'), type: 'string' },
    moves: { header: t('moves', 'Moves'), type: 'number' },

    id: { hide: true },
    originalRef: { hide: true },
  }

  const dataExport = {
    title: t('railVisits', 'Rail Visits'),
    enableDataExport: true,
    dataExportConfig,
  }

  const defaultOptions = useDefaultMRTOptions<RailVisitTableDto>(undefined, dataExport)

  const { savedPreferences, onPreferencesChange } =
    useTablePreferencesConfig('planning/rail-visits')

  const table = useTomTable<RailVisitTableDto>(
    {
      data: data,
      columns: columns,
      getRowId: row => row.id.toString(),
      enableTopToolbar: true,
      enableRowSelection: true,
      enableExpanding: true,
      getRowCanExpand: () => true,

      renderDetailPanel: ({ row }) => <RailVisitTableDetailPanel row={row} />,

      initialState: {
        ...defaultOptions.initialState,
        sorting: [{ id: 'etaAta', desc: true }],
        columnPinning: {
          left: ['mrt-row-actions', 'mrt-row-expand', 'mrt-row-select', 'id', 'name'],
        },
        pagination: {
          pageIndex: 0,
          pageSize: 50,
        },
        columnFilters: [
          {
            id: 'status',
            value: [
              CarrierVisitStatus.Expected,
              CarrierVisitStatus.Arrived,
              CarrierVisitStatus.InOperation,
              CarrierVisitStatus.Departed,
              CarrierVisitStatus.Completed,
            ],
          },
        ],
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 280px)',
          minHeight: 'calc(100vh - 280px)',
        },
      },

      renderTopToolbarCustomActions: renderTableCustomActions,

      renderRowActions: ({ row }) => (
        <RailVisitTableOptions visit={row.original.originalRef} onDelete={onDelete} />
      ),
    },
    undefined,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
})
